export type EnvironmentChoice = {
  id: string;
  name: string;
  slug: string;
  role: string;
  userId?: string;
  isPersonal?: boolean;
};

export type EnvironmentPreference = {
  dontShowAgain: boolean;
  defaultOrganizationId: string | null;
};

const STORAGE_PREFIX = "nevisign.env-pref.";

function storageKey(userKey: string): string {
  return `${STORAGE_PREFIX}${userKey}`;
}

export function environmentPreferenceKey(opts: {
  phone?: string | null;
  userId?: string | null;
}): string | null {
  const phone = opts.phone
    ?.replace(/[\u06F0-\u06F9]/g, (ch) => String(ch.charCodeAt(0) - 0x06f0))
    .replace(/[\u0660-\u0669]/g, (ch) => String(ch.charCodeAt(0) - 0x0660))
    .replace(/\D/g, "");
  if (phone && phone.length >= 10) return `phone:${phone}`;
  if (opts.userId) return `user:${opts.userId}`;
  return null;
}

export function readEnvironmentPreference(userKey: string): EnvironmentPreference {
  try {
    const raw = localStorage.getItem(storageKey(userKey));
    if (!raw) return { dontShowAgain: false, defaultOrganizationId: null };
    const parsed = JSON.parse(raw) as Partial<EnvironmentPreference>;
    return {
      dontShowAgain: Boolean(parsed.dontShowAgain),
      defaultOrganizationId:
        typeof parsed.defaultOrganizationId === "string"
          ? parsed.defaultOrganizationId
          : null,
    };
  } catch {
    return { dontShowAgain: false, defaultOrganizationId: null };
  }
}

export function writeEnvironmentPreference(
  userKey: string,
  pref: EnvironmentPreference,
): void {
  localStorage.setItem(storageKey(userKey), JSON.stringify(pref));
}

export function resolveAutoPickOrganizationId(
  environments: EnvironmentChoice[],
  userKey: string | null,
): string | null {
  if (!userKey || environments.length <= 1) return environments[0]?.id ?? null;
  const pref = readEnvironmentPreference(userKey);
  if (!pref.dontShowAgain || !pref.defaultOrganizationId) return null;
  const stillValid = environments.some((e) => e.id === pref.defaultOrganizationId);
  return stillValid ? pref.defaultOrganizationId : null;
}

export function environmentDisplayName(env: {
  name: string;
  isPersonal?: boolean;
}): string {
  if (env.isPersonal) return "محیط شخصی";
  return env.name;
}

export function isPersonalEnvironment(env: {
  slug?: string;
  isPersonal?: boolean;
  name?: string;
}): boolean {
  if (env.isPersonal) return true;
  const slug = (env.slug ?? "").toLowerCase();
  if (slug.includes("personal") || slug.includes("شخصی")) return true;
  const name = env.name ?? "";
  return name.includes("شخصی") || name.includes("حقیقی");
}
